const cleanLines = (source = "") => String(source)
  .replace(/\r\n/g, "\n")
  .split("\n")
  .map((line) => line.trim())
  .filter(Boolean);

const isShort = (value, max = 48) => value && value.length <= max;

const looksLikeEyebrow = (value) => {
  if (!isShort(value, 42)) return false;
  const letters = value.replace(/[^A-Za-zА-Яа-яЁё]/g, "");
  if (!letters) return false;
  const upper = letters.replace(/[^A-ZА-ЯЁ]/g, "").length;
  return upper / letters.length >= 0.65 || /·|\b\d{1,2}\b/.test(value);
};

export const HERO_LEAD_MAX_CHARS = 160;
export const HERO_LEAD_MAX_SENTENCES = 2;

const FIELD_LABELS = {
  eyebrow: "Надзаголовок",
  headline: "Заголовок",
  subtitle: "Подзаголовок",
  lead: "Короткий лид",
};

const splitSentences = (text) => {
  if (!text) return [];
  const matches = text.match(/[^.!?…]+[.!?…]+|[^.!?…]+$/g) || [];
  return matches.map((sentence) => sentence.trim()).filter(Boolean);
};

const truncateAtWord = (text, maxChars) => {
  if (text.length <= maxChars) {
    return { head: text, cutIndex: text.length };
  }
  const slice = text.slice(0, maxChars);
  const lastSpace = slice.lastIndexOf(" ");
  const cutIndex = lastSpace > 0 ? lastSpace : maxChars;
  const head = text.slice(0, cutIndex).trim();
  return { head: `${head}…`, cutIndex };
};

const buildLeadAndLeftover = (remainderText) => {
  const sentences = splitSentences(remainderText);

  if (!sentences.length) {
    return { lead: "", leftoverText: "" };
  }

  const leadSentences = [];
  let leadChars = 0;

  for (const sentence of sentences) {
    if (leadSentences.length >= HERO_LEAD_MAX_SENTENCES) break;

    if (leadSentences.length === 0 && sentence.length > HERO_LEAD_MAX_CHARS) {
      const { head, cutIndex } = truncateAtWord(sentence, HERO_LEAD_MAX_CHARS);
      const remainderOfSentence = sentence.slice(cutIndex).trim();
      const leftoverParts = [remainderOfSentence, ...sentences.slice(1)].filter(Boolean);
      return {
        lead: head,
        leftoverText: leftoverParts.join(" ").trim(),
      };
    }

    const nextChars = leadChars + sentence.length + (leadSentences.length ? 1 : 0);
    if (leadSentences.length > 0 && nextChars > HERO_LEAD_MAX_CHARS) break;

    leadSentences.push(sentence);
    leadChars = nextChars;
  }

  const leftoverSentences = sentences.slice(leadSentences.length);

  return {
    lead: leadSentences.join(" ").trim(),
    leftoverText: leftoverSentences.join(" ").trim(),
  };
};

/**
 * Deterministic first-pass HERO structuring.
 * A HERO is a cover: it keeps only eyebrow / headline / subtitle / a short lead.
 * Longer content is preserved separately as leftoverText for the next spread.
 */
export function structureHeroSource(source = "") {
  const lines = cleanLines(source);

  if (!lines.length) {
    return {
      eyebrow: "",
      headline: "",
      subtitle: "",
      lead: "",
      leftoverText: "",
      detectedFields: [],
    };
  }

  let cursor = 0;
  let eyebrow = "";

  if (lines.length > 1 && looksLikeEyebrow(lines[0])) {
    eyebrow = lines[0];
    cursor += 1;
  }

  const headline = lines[cursor] || "";
  cursor += headline ? 1 : 0;

  let subtitle = "";

  if (lines[cursor] && isShort(lines[cursor], 90)) {
    subtitle = lines[cursor];
    cursor += 1;
  }

  const remainderText = lines.slice(cursor).join(" ").trim();
  const { lead, leftoverText } = buildLeadAndLeftover(remainderText);

  const detectedFields = [];
  if (eyebrow) detectedFields.push(FIELD_LABELS.eyebrow);
  if (headline) detectedFields.push(FIELD_LABELS.headline);
  if (subtitle) detectedFields.push(FIELD_LABELS.subtitle);
  if (lead) detectedFields.push(FIELD_LABELS.lead);

  return {
    eyebrow,
    headline,
    subtitle,
    lead,
    leftoverText,
    detectedFields,
  };
}

export function heroUsesPrimaryImageOnMobile(content = {}) {
  return !content.hero_mobile_media_id && !content.hero_mobile_url;
}

export function buildHeroSourceExtra(source = "", structured = null) {
  const parsed = structured || structureHeroSource(source);
  return {
    hero_source_text: String(source || ""),
    hero_leftover_text: parsed.leftoverText || "",
  };
}

export function deriveDetectedFieldsFromContent(content = {}) {
  const detectedFields = [];
  if (content.eyebrow) detectedFields.push(FIELD_LABELS.eyebrow);
  if (content.headline) detectedFields.push(FIELD_LABELS.headline);
  if (content.subtitle) detectedFields.push(FIELD_LABELS.subtitle);
  if (content.lead) detectedFields.push(FIELD_LABELS.lead);
  return detectedFields;
}
